/** Shared NFA primitives — pill, panel, dense table shell, slide-over. */
import { X } from 'lucide-react';
import { STATUS_TONE } from './nfaTheme';

export const Pill = ({ value, T, testId }) => {
  const tone = STATUS_TONE[value] || 'muted';
  const c = { muted: T.muted, primary: T.primary, amber: T.amber, green: T.green,
    red: T.red, accent: T.accent }[tone];
  return (
    <span data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide whitespace-nowrap"
      style={{ background: T.tint(c), color: c, border: `1px solid ${T.tint(c, '33')}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {String(value || '—').replace(/_/g, ' ')}
    </span>
  );
};

export const Panel = ({ title, subtitle, right, children, T, testId, pad = true }) => (
  <section data-testid={testId} className="rounded-lg overflow-hidden"
    style={{ background: T.surface, border: `1px solid ${T.border}` }}>
    {(title || right) && (
      <header className="px-4 py-2.5 flex items-center gap-3 flex-wrap"
        style={{ borderBottom: `1px solid ${T.border}`, background: T.surfaceAlt }}>
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold leading-tight"
            style={{ color: T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>{title}</h3>
          {subtitle && <p className="text-[10.5px] mt-0.5" style={{ color: T.muted }}>{subtitle}</p>}
        </div>
        {right && <div className="ml-auto flex items-center gap-2 flex-wrap">{right}</div>}
      </header>
    )}
    <div className={pad ? 'p-4' : ''}>{children}</div>
  </section>
);

export const Stat = ({ label, value, tone, sub, T, testId, info }) => (
  <div data-testid={testId} className="rounded-lg px-3 py-2.5 relative group"
    style={{ background: T.surface, border: `1px solid ${T.border}` }}>
    <div className="text-[9.5px] uppercase tracking-[0.08em] flex items-center gap-1"
      style={{ color: T.muted }}>
      {label}
      {info && (
        <span tabIndex={0} data-testid={testId ? `${testId}-info` : undefined}
          className="inline-flex items-center justify-center w-3 h-3 rounded-full text-[7px] font-bold cursor-help shrink-0"
          style={{ border: `1px solid ${T.border}`, color: T.muted }}>i</span>
      )}
    </div>
    <div className="text-[19px] font-semibold tabular-nums leading-tight mt-0.5"
      style={{ color: tone || T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>{value}</div>
    {sub && <div className="text-[10px] mt-0.5" style={{ color: T.muted }}>{sub}</div>}
    {info && (
      <div role="tooltip" data-testid={testId ? `${testId}-definition` : undefined}
        className="absolute left-0 top-full mt-1 z-50 w-64 px-2.5 py-2 rounded-md text-[10.5px] leading-relaxed opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity pointer-events-none"
        style={{ background: T.isDark ? '#18181B' : '#0F172A', color: '#FFFFFF',
          border: `1px solid ${T.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.28)' }}>
        {info}
      </div>
    )}
  </div>
);

export const Btn = ({ children, onClick, variant = 'primary', T, disabled, testId, type = 'button' }) => {
  const style = {
    primary: { background: T.primary, color: '#fff', border: `1px solid ${T.primary}` },
    ghost: { background: 'transparent', color: T.body, border: `1px solid ${T.border}` },
    danger: { background: T.tint(T.red, '1F'), color: T.red, border: `1px solid ${T.tint(T.red, '44')}` },
    ok: { background: T.tint(T.green, '1F'), color: T.green, border: `1px solid ${T.tint(T.green, '44')}` },
    warn: { background: T.tint(T.accent, '1F'), color: T.accent, border: `1px solid ${T.tint(T.accent, '44')}` },
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} data-testid={testId}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold transition-opacity hover:opacity-85 disabled:opacity-40"
      style={style}>{children}</button>
  );
};

export const Field = ({ label, children, T, hint }) => (
  <label className="block">
    <span className="text-[9.5px] uppercase tracking-[0.08em] block mb-1" style={{ color: T.muted }}>{label}</span>
    {children}
    {hint && <span className="text-[9.5px] block mt-1" style={{ color: T.muted }}>{hint}</span>}
  </label>
);

export const inputStyle = (T) => ({
  background: T.isDark ? T.surfaceAlt : '#FFFFFF',
  border: `1px solid ${T.border}`, color: T.ink,
});

export const Input = (props) => {
  const { T, ...rest } = props;
  return <input {...rest} className="w-full text-[12px] rounded-md px-2.5 py-1.5 outline-none focus:ring-1"
    style={{ ...inputStyle(T), ...(props.style || {}) }} />;
};

export const Select = ({ T, children, ...rest }) => (
  <select {...rest} className="w-full text-[12px] rounded-md px-2.5 py-1.5 outline-none"
    style={inputStyle(T)}>{children}</select>
);

export const Drawer = ({ open, onClose, title, subtitle, children, T, testId, width = 760 }) => {
  if (!open) return null;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-[80]"
        style={{ background: 'rgba(15,23,41,0.42)', backdropFilter: 'blur(1.5px)' }} />
      <aside data-testid={testId}
        className="fixed right-0 top-0 z-[81] flex flex-col shadow-2xl"
        style={{ width: `min(${width}px, 96vw)`, height: '100dvh', background: T.page,
          borderLeft: `1px solid ${T.border}` }}>
        <header className="px-5 py-3.5 flex items-start gap-3 shrink-0"
          style={{ background: T.surface, borderBottom: `1px solid ${T.border}` }}>
          <div className="min-w-0">
            <h2 className="text-[15px] font-bold leading-tight truncate"
              style={{ color: T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>{title}</h2>
            {subtitle}
          </div>
          <button type="button" onClick={onClose} className="ml-auto p-1 rounded hover:opacity-70"
            data-testid="nfa-drawer-close"><X className="w-4 h-4" style={{ color: T.muted }} /></button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">{children}</div>
      </aside>
    </>
  );
};

export const Empty = ({ children, T, testId }) => (
  <div data-testid={testId} className="py-10 text-center text-[11.5px]" style={{ color: T.muted }}>
    {children}
  </div>
);
